import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import CloseIcon from '../assets/x.svg';
import ProgramIcon from '../assets/icons/program.svg';
import TrophyIcon from '../assets/icons/trophy.svg';
import NotificationIcon from '../assets/icons/notification.svg';
import SettingsIcon from '../assets/icons/settings.svg';
import ClubIcon from '../assets/icons/clipboard.svg';
import LogoutIcon from '../assets/icons/logout.svg';
import '../styles/MorePanel.css';

function MorePanel({ isOpen, onClose, userId, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const isActive = (path) => location.pathname === path;

  // Handle open/close with animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsClosing(false);
    } else if (isVisible) {
      // Start closing animation
      setIsClosing(true);
      // Wait for animation to complete before hiding
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsClosing(false);
      }, 250); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  // Close panel on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
      onClose();
    } catch (error) {
      console.error('[MorePanel] Error signing out:', error);
    }
  };

  const menuItems = [
    {
      id: 'program',
      icon: ProgramIcon,
      label: 'Program',
      path: `/${userId}/program`,
    },
    {
      id: 'awards',
      icon: TrophyIcon,
      label: 'Awards',
      path: `/${userId}/awards`,
    },
    ...(user?.ownedClubIds?.length > 0 ? [{
      id: 'club',
      icon: ClubIcon,
      label: 'Club',
      path: `/${userId}/club/${user.ownedClubIds[0]}`,
    }] : []),
    {
      id: 'notifications',
      icon: NotificationIcon,
      label: 'Notifications',
      path: `/${userId}/notifications`,
    },
    {
      id: 'settings',
      icon: SettingsIcon,
      label: 'Settings',
      path: `/${userId}/settings`,
    },
  ];

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className={`more-panel-overlay ${isClosing ? 'closing' : ''}`} 
        onClick={onClose} 
      />
      
      {/* Panel */}
      <div className={`more-panel ${isClosing ? 'closing' : ''}`}>
        <div className="more-panel-header">
          <div className="more-panel-profile">
            {user?.profilePictureUrl ? (
              <img 
                src={user.profilePictureUrl} 
                alt="Profile" 
                className="more-panel-avatar"
              />
            ) : (
              <div className="more-panel-avatar-placeholder">
                {(user?.firstName?.[0] || user?.email?.[0] || 'U').toUpperCase()}
              </div>
            )}
            <div className="more-panel-user-info">
              <span className="more-panel-name">
                {user?.firstName || 'User'}
              </span>
              <span className="more-panel-email">
                {user?.email || ''}
              </span>
            </div>
          </div>
          <button className="more-panel-close" onClick={onClose} aria-label="Close">
            <img src={CloseIcon} alt="Close" />
          </button>
        </div>

        <div className="more-panel-menu">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`more-panel-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => handleNavigation(item.path)}
            >
              <img src={item.icon} alt="" className="more-panel-item-icon" />
              <span className="more-panel-item-label">{item.label}</span>
            </button>
          ))}
        </div>

        <div className="more-panel-footer">
          <button className="more-panel-logout" onClick={handleLogout}>
            <img src={LogoutIcon} alt="" className="more-panel-item-icon" />
            <span className="more-panel-item-label">Log Out</span>
          </button>
        </div>
      </div>
    </>
  );
}

export default MorePanel;

